import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { 
  Users, CheckCircle, XCircle, Clock, DollarSign, Award, 
  ChevronDown, ChevronUp, Eye, RefreshCw, Search, Filter,
  TrendingUp, Wallet
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminReferralsSection = ({ token }) => {
  const [loading, setLoading] = useState(true);
  const [partners, setPartners] = useState([]);
  const [stats, setStats] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedPartner, setExpandedPartner] = useState(null);
  const [partnerDetails, setPartnerDetails] = useState({});
  const [counts, setCounts] = useState({ pending: 0, approved: 0, rejected: 0 });
  const [paymentAmount, setPaymentAmount] = useState('');

  const fetchPartners = useCallback(async () => {
    setLoading(true);
    try {
      const url = new URL(`${API_URL}/api/admin/referral-partners`);
      if (statusFilter) url.searchParams.append('status', statusFilter);
      
      const res = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to fetch partners');
      
      setPartners(data.partners);
      setCounts(data.counts);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [token, statusFilter]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/referral-stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to fetch stats');
      setStats(data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  }, [token]);

  const fetchPartnerDetails = async (partnerId) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/referral-partners/${partnerId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to fetch details');
      setPartnerDetails(prev => ({ ...prev, [partnerId]: data }));
    } catch (err) {
      toast.error(err.message);
    }
  };

  useEffect(() => {
    fetchPartners();
    fetchStats();
  }, [fetchPartners, fetchStats]);

  const handleApprove = async (partnerId) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/referral-partners/${partnerId}/approve`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to approve');
      toast.success(data.message);
      fetchPartners();
      fetchStats();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleReject = async (partnerId) => {
    const reason = window.prompt('Reason for rejection (optional):');
    try {
      const url = new URL(`${API_URL}/api/admin/referral-partners/${partnerId}/reject`);
      if (reason) url.searchParams.append('reason', reason);
      
      const res = await fetch(url, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to reject');
      toast.success(data.message);
      fetchPartners();
      fetchStats();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleMarkPaid = async (partnerId, pendingAmount) => {
    const amount = parseFloat(paymentAmount || pendingAmount);
    if (isNaN(amount) || amount <= 0) {
      toast.error('Invalid amount');
      return;
    }
    
    try {
      const url = new URL(`${API_URL}/api/admin/referral-partners/${partnerId}/mark-paid`);
      url.searchParams.append('amount', amount.toString());
      
      const res = await fetch(url, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to mark paid');
      toast.success(data.message);
      setPaymentAmount('');
      fetchPartners();
      fetchStats();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const toggleExpand = (partnerId) => {
    if (expandedPartner === partnerId) {
      setExpandedPartner(null);
    } else {
      setExpandedPartner(partnerId);
      if (!partnerDetails[partnerId]) {
        fetchPartnerDetails(partnerId);
      }
    }
  };

  const getTierBadge = (tier) => {
    const colors = {
      gold: 'bg-yellow-100 text-yellow-800',
      silver: 'bg-gray-100 text-gray-800',
      bronze: 'bg-amber-100 text-amber-800'
    };
    return colors[tier] || colors.bronze;
  };

  const getStatusBadge = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const filteredPartners = partners.filter(p => 
    !searchQuery || 
    p.full_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.referral_code.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Stats Overview */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl p-4 shadow-sm border">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">Total Partners</span>
              <Users className="text-blue-500" size={20} />
            </div>
            <p className="text-2xl font-bold mt-2">{stats.overview.total_partners}</p>
            <p className="text-xs text-yellow-600">{stats.overview.pending_partners} pending</p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">Total Referrals</span>
              <TrendingUp className="text-green-500" size={20} />
            </div>
            <p className="text-2xl font-bold mt-2">{stats.referrals.total}</p>
            <p className="text-xs text-green-600">{stats.referrals.successful} successful</p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">Total Commissions</span>
              <DollarSign className="text-emerald-500" size={20} />
            </div>
            <p className="text-2xl font-bold mt-2">₹{stats.earnings.total.toLocaleString()}</p>
            <p className="text-xs text-orange-600">₹{stats.earnings.pending.toLocaleString()} pending</p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">Paid Out</span>
              <Wallet className="text-purple-500" size={20} />
            </div>
            <p className="text-2xl font-bold mt-2">₹{stats.earnings.paid.toLocaleString()}</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
        <div className="flex gap-2">
          <button 
            onClick={() => setStatusFilter('')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              !statusFilter ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            All ({counts.pending + counts.approved + counts.rejected})
          </button>
          <button 
            onClick={() => setStatusFilter('pending')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              statusFilter === 'pending' ? 'bg-yellow-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Pending ({counts.pending})
          </button>
          <button 
            onClick={() => setStatusFilter('approved')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              statusFilter === 'approved' ? 'bg-green-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Approved ({counts.approved})
          </button>
          <button 
            onClick={() => setStatusFilter('rejected')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              statusFilter === 'rejected' ? 'bg-red-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Rejected ({counts.rejected})
          </button>
        </div>
        <div className="flex gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Search partners..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <button
            onClick={() => { fetchPartners(); fetchStats(); }}
            className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <RefreshCw size={18} />
          </button>
        </div>
      </div>

      {/* Partners List */}
      {loading ? (
        <div className="text-center py-12">
          <RefreshCw className="animate-spin mx-auto text-blue-500" size={32} />
          <p className="text-gray-500 mt-2">Loading partners...</p>
        </div>
      ) : filteredPartners.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-xl">
          <Users className="mx-auto text-gray-400" size={48} />
          <p className="text-gray-500 mt-2">No referral partners found</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredPartners.map((partner) => (
            <div key={partner.id} className="bg-white rounded-xl shadow-sm border overflow-hidden">
              {/* Partner Header */}
              <div 
                className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50"
                onClick={() => toggleExpand(partner.id)}
              >
                <div className="flex items-center gap-4">
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold">{partner.full_name}</h3>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${getStatusBadge(partner.status)}`}>
                        {partner.status}
                      </span>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${getTierBadge(partner.tier)}`}>
                        {partner.tier}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500">{partner.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <p className="font-mono font-bold text-blue-600">{partner.referral_code}</p>
                    <p className="text-sm text-gray-500">
                      {partner.successful_referrals} successful • ₹{partner.total_earnings.toLocaleString()}
                    </p>
                  </div>
                  {expandedPartner === partner.id ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </div>
              </div>

              {/* Expanded Details */}
              {expandedPartner === partner.id && (
                <div className="border-t px-4 py-4 bg-gray-50">
                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Partner Info */}
                    <div>
                      <h4 className="font-medium mb-3">Partner Details</h4>
                      <div className="space-y-2 text-sm">
                        <p><span className="text-gray-500">Phone:</span> {partner.phone}</p>
                        <p><span className="text-gray-500">Organization:</span> {partner.organization || 'N/A'}</p>
                        <p><span className="text-gray-500">Reason:</span> {partner.reason || 'N/A'}</p>
                        <p><span className="text-gray-500">Created:</span> {new Date(partner.created_at).toLocaleString()}</p>
                        {partner.approved_at && (
                          <p><span className="text-gray-500">Approved:</span> {new Date(partner.approved_at).toLocaleString()}</p>
                        )}
                        {partner.rejected_at && (
                          <>
                            <p><span className="text-gray-500">Rejected:</span> {new Date(partner.rejected_at).toLocaleString()}</p>
                            {partner.rejection_reason && (
                              <p><span className="text-gray-500">Reason:</span> {partner.rejection_reason}</p>
                            )}
                          </>
                        )}
                      </div>
                    </div>

                    {/* Earnings & Actions */}
                    <div>
                      <h4 className="font-medium mb-3">Earnings & Actions</h4>
                      <div className="grid grid-cols-3 gap-3 mb-4">
                        <div className="bg-white p-3 rounded-lg border">
                          <p className="text-xs text-gray-500">Total Referrals</p>
                          <p className="font-bold">{partner.total_referrals}</p>
                        </div>
                        <div className="bg-white p-3 rounded-lg border">
                          <p className="text-xs text-gray-500">Pending</p>
                          <p className="font-bold text-orange-600">₹{partner.pending_earnings.toLocaleString()}</p>
                        </div>
                        <div className="bg-white p-3 rounded-lg border">
                          <p className="text-xs text-gray-500">Paid</p>
                          <p className="font-bold text-green-600">₹{partner.paid_earnings.toLocaleString()}</p>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex flex-wrap gap-2">
                        {partner.status === 'pending' && (
                          <>
                            <button
                              onClick={(e) => { e.stopPropagation(); handleApprove(partner.id); }}
                              className="flex items-center gap-1 px-3 py-2 bg-green-500 text-white rounded-lg text-sm hover:bg-green-600"
                            >
                              <CheckCircle size={16} /> Approve
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); handleReject(partner.id); }}
                              className="flex items-center gap-1 px-3 py-2 bg-red-500 text-white rounded-lg text-sm hover:bg-red-600"
                            >
                              <XCircle size={16} /> Reject
                            </button>
                          </>
                        )}
                        {partner.status === 'approved' && partner.pending_earnings > 0 && (
                          <div className="flex items-center gap-2">
                            <input
                              type="number"
                              placeholder={`Max ₹${partner.pending_earnings}`}
                              value={paymentAmount}
                              onChange={(e) => setPaymentAmount(e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                              className="w-32 px-3 py-2 border rounded-lg text-sm"
                            />
                            <button
                              onClick={(e) => { e.stopPropagation(); handleMarkPaid(partner.id, partner.pending_earnings); }}
                              className="flex items-center gap-1 px-3 py-2 bg-blue-500 text-white rounded-lg text-sm hover:bg-blue-600"
                            >
                              <Wallet size={16} /> Mark Paid
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Recent Registrations */}
                  {partnerDetails[partner.id]?.registrations?.length > 0 && (
                    <div className="mt-4 pt-4 border-t">
                      <h4 className="font-medium mb-3">Registrations via this Partner</h4>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="bg-gray-100">
                              <th className="px-3 py-2 text-left">Team</th>
                              <th className="px-3 py-2 text-left">Leader</th>
                              <th className="px-3 py-2 text-right">Amount</th>
                              <th className="px-3 py-2 text-right">Commission</th>
                              <th className="px-3 py-2 text-center">Status</th>
                              <th className="px-3 py-2 text-left">Date</th>
                            </tr>
                          </thead>
                          <tbody>
                            {partnerDetails[partner.id].registrations.map((reg) => (
                              <tr key={reg.id} className="border-b">
                                <td className="px-3 py-2">{reg.team_name}</td>
                                <td className="px-3 py-2">{reg.leader_name}</td>
                                <td className="px-3 py-2 text-right">₹{reg.registration_amount}</td>
                                <td className="px-3 py-2 text-right text-green-600">₹{reg.commission_amount}</td>
                                <td className="px-3 py-2 text-center">
                                  <span className={`px-2 py-0.5 rounded text-xs ${getStatusBadge(reg.status)}`}>
                                    {reg.status}
                                  </span>
                                </td>
                                <td className="px-3 py-2">{new Date(reg.registered_at).toLocaleDateString()}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Top Performers */}
      {stats?.top_performers?.length > 0 && (
        <div className="bg-white rounded-xl p-6 shadow-sm border">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <Award className="text-yellow-500" size={20} />
            Top Performers
          </h3>
          <div className="grid md:grid-cols-5 gap-4">
            {stats.top_performers.map((p, idx) => (
              <div key={idx} className="text-center p-3 bg-gray-50 rounded-lg">
                <div className={`w-8 h-8 rounded-full mx-auto mb-2 flex items-center justify-center ${
                  idx === 0 ? 'bg-yellow-400' : idx === 1 ? 'bg-gray-300' : 'bg-amber-400'
                }`}>
                  <span className="font-bold text-white">{idx + 1}</span>
                </div>
                <p className="font-medium text-sm">{p.full_name}</p>
                <p className="text-xs text-gray-500">{p.referral_code}</p>
                <p className="text-sm font-bold text-green-600">{p.successful_referrals} referrals</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminReferralsSection;
